'use client'

import { useState, useEffect } from 'react'
import { useThemeStore } from '@/lib/stores/theme-store'
import { Skeleton } from '@/components/ui/skeleton'
import { getLogoFromConfig, getLoginBackgroundFromConfig } from '@/lib/theme-helpers'
import { getWhiteLabelDomain } from '@/lib/utils'

interface WhiteLabelAssetsProps {
  type: 'logo' | 'logo-light' | 'logo-form' | 'background'
  objectPosition?: 'center' | 'left' | 'right' | 'top' | 'bottom' | 'center center' | 'left center' | 'right center' | 'top center' | 'bottom center' | 'center left' | 'center right' | 'center top' | 'center bottom'
  className?: string
  alt?: string
  priority?: boolean
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void
}

// Default fallback assets (somente para domínios não white-label)
const DEFAULT_ASSETS = {
  logo: 'https://worldwide-pullzone.b-cdn.net/logo-dark.png',
  'logo-light': 'https://worldwide-pullzone.b-cdn.net/Mentorfy%20Logo.png',
  'logo-form': 'https://worldwide-pullzone.b-cdn.net/logo-dark.png',
  background: 'https://worldwide-pullzone.b-cdn.net/342b91f971f895c3e77cd78d4ff8a0bdfe4a197c.jpg'
}

export function WhiteLabelAssets({ type, className, alt, priority = false, onLoad, objectPosition = 'center' }: WhiteLabelAssetsProps) {
  const { currentTheme, isLoading, isInitialized } = useThemeStore()
  const [mounted, setMounted] = useState(false)
  const [isWhiteLabel, setIsWhiteLabel] = useState(false)

  // Detectar white-label apenas no cliente após montagem (evita hydration mismatch)
  useEffect(() => {
    setMounted(true)
    setIsWhiteLabel(getWhiteLabelDomain(window.location.hostname) !== null)
  }, [])

  // Durante SSR e antes da montagem, mostrar skeleton para evitar hydration mismatch
  if (!mounted) {
    if (type === 'background') {
      return <div className={className} style={{ backgroundColor: 'transparent' }} />
    }
    return <Skeleton className={className} />
  }

  // Se é white-label e tema ainda não está pronto, não mostrar nada (evita flash de default)
  const themeReady = !isWhiteLabel || (isInitialized && currentTheme)

  // Mostrar skeleton enquanto carrega (exceto background que fica transparente)
  if (isLoading || (!themeReady && isWhiteLabel)) {
    if (type === 'background') {
      return <div className={className} style={{ backgroundColor: 'transparent' }} />
    }
    return <Skeleton className={className} />
  }

  // Determinar URL do asset
  let assetUrl: string | null | undefined = null

  if (currentTheme) {
    switch (type) {
      case 'logo':
      case 'logo-light':
        assetUrl = getLogoFromConfig(currentTheme, 'header')
        break
      case 'logo-form':
        assetUrl = getLogoFromConfig(currentTheme, 'form')
        break
      case 'background':
        assetUrl = getLoginBackgroundFromConfig(currentTheme)
        break
    }
  }

  // Fallback para default apenas se NÃO for white-label ou se tema carregou mas não tem a imagem
  if (!assetUrl) {
    if (!isWhiteLabel || (isInitialized && !currentTheme)) {
      assetUrl = DEFAULT_ASSETS[type]
    } else {
      // White-label sem imagem definida: não mostrar nada
      if (type === 'background') {
        return <div className={className} style={{ backgroundColor: 'transparent' }} />
      }
      return null
    }
  }

  if (type === 'background') {
    return (
      <img
        src={assetUrl}
        alt={alt || 'Background'}
        className={className}
        style={{
          objectFit: 'cover',
          objectPosition: objectPosition,
        }}
        {...(priority && { fetchPriority: 'high' as const })}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        onLoad={onLoad}
      />
    )
  }

  const logoAlt = alt || `${currentTheme?.title || 'Mentorfy'} ${type}`

  // PNG transparente fica sobre a superfície da página (paleta do Brand Studio), sem placa.
  return (
    <img
      src={assetUrl}
      alt={logoAlt}
      className={className}
      onLoad={onLoad}
    />
  )
}

interface BrandedContentProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

export function BrandedContent({ children, fallback }: BrandedContentProps) {
  const { currentTheme, isLoading } = useThemeStore()

  if (isLoading) {
    return fallback !== undefined ? fallback : <Skeleton className="h-20 w-40" />
  }

  const brandName = currentTheme?.title || 'Mentorfy'

  return (
    <div data-brand={brandName.toLowerCase().replace(/\s+/g, '-')}>
      {children}
    </div>
  )
}